import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageMeta from "@/components/feature/PageMeta";
import FilterBar from "@/components/feature/FilterBar";
import PosterCard from "@/components/feature/PosterCard";
import Pagination from "@/components/base/Pagination";
import { applyFilter, defaultFilter, getTitlesByType, typeLabels, type FilterState, type MediaType } from "@/lib/catalog";

const PAGE_SIZE = 6;

const categoryCopy: Record<MediaType, { title: string; intro: string }> = {
  movie: {
    title: "日韩电影",
    intro: "汇集日本与韩国最新上映电影，涵盖悬疑、爱情、剧情、家庭等类型，按年份与热度筛选，快速找到想看的那一部。",
  },
  drama: {
    title: "日韩电视剧",
    intro: "整理日本与韩国热播电视剧，连载更新与完结剧集一网打尽，可按题材与年份筛选，追剧不迷路。",
  },
};

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const mediaType: MediaType = type === "drama" ? "drama" : "movie";
  const copy = categoryCopy[mediaType];

  const [filter, setFilter] = useState<FilterState>(defaultFilter);
  const pageParam = Number(searchParams.get("page") || "1");

  const pool = useMemo(() => getTitlesByType(mediaType), [mediaType]);
  const filtered = useMemo(() => applyFilter(pool, filter), [pool, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, pageParam || 1), totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage, filter]);

  const goPage = (page: number) => {
    if (page <= 1) {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ page: String(page) }, { replace: true });
    }
  };

  const handleFilter = (next: FilterState) => {
    setFilter(next);
    setSearchParams({}, { replace: true });
  };

  return (
    <>
      <PageMeta
        title={`${copy.title} - 在线观看日韩影视作品 -日韩在线`}
        description={`${copy.title}分类页共收录 ${pool.length} 部作品，${copy.intro.slice(0, 60)}…支持按年份、地区、题材与热度筛选。`}
        keywords={`日韩电影,日韩电视剧,${copy.title},在线观看日韩影视,日韩剧集`}
      />
      <SiteLayout topOffset>
        {/* Header */}
        <section className="relative w-full bg-primary-950 overflow-hidden">
          <div className="absolute inset-0 opacity-30">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20cinematic%20dark%20navy%20blue%20background%20with%20soft%20amber%20light%20streaks%20and%20subtle%20film%20grain%2C%20minimal%20artistic%20texture%2C%20wide%20banner%20composition%2C%20elegant%20moody%20atmosphere&width=1920&height=520&seq=jk-category-header-01&orientation=landscape"
              alt={`${copy.title}分类横幅`}
              title={`${copy.title} 在线观看日韩影视作品`}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative w-full px-4 md:px-8 py-10 md:py-14">
            <nav className="flex items-center gap-1.5 text-xs text-background-200/70 flex-wrap" aria-label="面包屑">
              <Link to="/" className="hover:text-background-50 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center" />
              <span className="text-background-50">{copy.title}</span>
            </nav>
            <div className="mt-5 flex flex-col lg:flex-row lg:items-end justify-between gap-5">
              <div className="max-w-2xl">
                <div className="flex items-center gap-2 mb-2.5">
                  <span className="w-6 h-[3px] rounded-full bg-accent-500" />
                  <span className="text-[11px] font-label font-semibold tracking-[0.2em] uppercase text-accent-400">
                    {mediaType === "movie" ? "Movies" : "Drama Series"}
                  </span>
                </div>
                <h1 className="font-heading text-2xl md:text-4xl font-black text-background-50 tracking-tight">
                  {copy.title}
                </h1>
                <p className="mt-3 text-sm text-background-200/80 leading-relaxed">{copy.intro}</p>
              </div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <span className="px-3 py-1.5 rounded-md bg-background-50/10 border border-background-50/15 text-[11px] font-label text-background-100">
                  共 {pool.length} 部作品
                </span>
                <span className="px-3 py-1.5 rounded-md bg-background-50/10 border border-background-50/15 text-[11px] font-label text-background-100">
                  当前筛选 {filtered.length} 部
                </span>
                <Link
                  to={mediaType === "movie" ? "/category/drama" : "/category/movie"}
                  className="px-3 py-1.5 rounded-md bg-accent-500 text-[11px] font-label font-semibold text-background-50 whitespace-nowrap hover:bg-accent-600 transition-colors"
                >
                  切换到{typeLabels[mediaType === "movie" ? "drama" : "movie"]}
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-8 md:py-10">
          <FilterBar filter={filter} onChange={handleFilter} />

          <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-foreground-500">
              第 {currentPage} 页 / 共 {totalPages} 页 · 共 {filtered.length} 部
            </p>
            <div className="flex items-center gap-1.5 text-xs text-foreground-500">
              <i className="ri-sort-desc w-3.5 h-3.5 flex items-center justify-center" />
              按「{filter.sort}」排序
            </div>
          </div>

          {pageItems.length === 0 ? (
            <div className="mt-4 rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
              <span className="w-14 h-14 rounded-full bg-background-200 text-foreground-400 flex items-center justify-center mx-auto">
                <i className="ri-filter-off-line text-2xl w-6 h-6 flex items-center justify-center" />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-900">暂无匹配影视，尝试重置筛选</h3>
              <p className="mt-1.5 text-xs text-foreground-500">当前筛选条件过于严格，换个年份或题材试试看。</p>
              <button
                type="button"
                onClick={() => handleFilter({ ...defaultFilter })}
                className="mt-5 inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary-600 text-background-50 text-xs font-label font-semibold whitespace-nowrap hover:bg-primary-700 transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line w-4 h-4 flex items-center justify-center" />
                重置筛选条件
              </button>
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-7 md:gap-x-5 md:gap-y-9">
              {pageItems.map((item) => (
                <PosterCard key={item.id} item={item} showSynopsis />
              ))}
            </div>
          )}

          <Pagination current={currentPage} totalPages={totalPages} onChange={goPage} />
        </section>
      </SiteLayout>
    </>
  );
}